"use client";

import { getAuthHeaders } from "@/infrastructure/http/authHeaders";
import { createIdempotencyKey } from "@/infrastructure/http/request-id";
import { httpClient } from "@/infrastructure/http/HttpClient";
import type {
  CreateProjectReportRequest,
  ProjectReport,
  UpdateProjectReportRequest,
} from "@/types/project-reports";

const API_BASE_URL =
  (process.env.NEXT_PUBLIC_CUSTDEV_API_URL || "").replace(/\/$/, "") ||
  "http://localhost:8200";

function parseErrorDetail(detail: unknown): string {
  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }
        if (
          item &&
          typeof item === "object" &&
          "msg" in item &&
          typeof (item as { msg?: unknown }).msg === "string"
        ) {
          return (item as { msg: string }).msg;
        }
        return "";
      })
      .filter(Boolean)
      .join(", ");
  }

  return "";
}

function isUuidValidationError(detail: unknown): boolean {
  if (!Array.isArray(detail)) {
    return false;
  }

  return detail.some(
    (item) =>
      item &&
      typeof item === "object" &&
      "type" in item &&
      (item as { type?: string }).type === "uuid_parsing"
  );
}

async function requestProjectReports<T>(
  url: string,
  context: string,
  options: RequestInit,
  opts: {
    requiresAuth?: boolean;
    idempotencyKey?: string;
    uuidErrorMessage?: string;
  } = {}
): Promise<T> {
  const headers = new Headers(options.headers);
  if (opts.requiresAuth) {
    const authHeaders = await getAuthHeaders();
    Object.entries(authHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });
  }

  const response = await httpClient.request(
    url,
    {
      ...options,
      headers,
    },
    {
      idempotencyKey: opts.idempotencyKey,
    }
  );

  if (!response.ok) {
    const rawText = await response.text().catch(() => "");
    let detail: unknown = rawText;

    if (rawText) {
      try {
        const parsed = JSON.parse(rawText) as { detail?: unknown };
        detail = parsed.detail ?? parsed;
      } catch {
        detail = rawText;
      }
    }

    if (isUuidValidationError(detail) && opts.uuidErrorMessage) {
      throw new Error(opts.uuidErrorMessage);
    }

    const parsedDetail = parseErrorDetail(detail);
    if (parsedDetail) {
      throw new Error(`${context}: ${parsedDetail}`);
    }

    throw new Error(`${context}: ${response.status} ${response.statusText}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function getProjectReports(
  projectId: string
): Promise<ProjectReport[]> {
  const url = `${API_BASE_URL}/projects/${projectId}/reports`;
  return requestProjectReports<ProjectReport[]>(
    url,
    "Failed to fetch project reports",
    { method: "GET" },
    {
      requiresAuth: true,
      uuidErrorMessage: "Invalid project ID format. Please use a valid project ID.",
    }
  );
}

export async function getProjectReport(
  projectId: string,
  reportId: string
): Promise<ProjectReport> {
  const url = `${API_BASE_URL}/projects/${projectId}/reports/${reportId}`;
  return requestProjectReports<ProjectReport>(
    url,
    "Failed to fetch project report",
    { method: "GET" },
    {
      requiresAuth: true,
      uuidErrorMessage: "Invalid project ID format. Please use a valid project ID.",
    }
  );
}

export async function getPublicProjectReport(
  projectId: string,
  reportId: string
): Promise<ProjectReport> {
  const url = `${API_BASE_URL}/projects/public/${projectId}/reports/${reportId}`;
  return requestProjectReports<ProjectReport>(
    url,
    "Failed to fetch public project report",
    { method: "GET" }
  );
}

export async function createProjectReport(
  projectId: string,
  payload: CreateProjectReportRequest
): Promise<ProjectReport> {
  const url = `${API_BASE_URL}/projects/${projectId}/reports`;
  return requestProjectReports<ProjectReport>(
    url,
    "Failed to create project report",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
    {
      requiresAuth: true,
      idempotencyKey: createIdempotencyKey("project-report-create", projectId, payload.title),
      uuidErrorMessage: "Invalid project ID format. Please use a valid project ID.",
    }
  );
}

export async function updateProjectReport(
  projectId: string,
  reportId: string,
  payload: UpdateProjectReportRequest
): Promise<ProjectReport> {
  const url = `${API_BASE_URL}/projects/${projectId}/reports/${reportId}`;
  return requestProjectReports<ProjectReport>(
    url,
    "Failed to update project report",
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
    {
      requiresAuth: true,
      idempotencyKey: createIdempotencyKey("project-report-update", projectId, reportId),
      uuidErrorMessage: "Invalid project ID format. Please use a valid project ID.",
    }
  );
}

export async function deleteProjectReport(
  projectId: string,
  reportId: string
): Promise<void> {
  const url = `${API_BASE_URL}/projects/${projectId}/reports/${reportId}`;
  await requestProjectReports<void>(
    url,
    "Failed to delete project report",
    {
      method: "DELETE",
    },
    {
      requiresAuth: true,
      idempotencyKey: createIdempotencyKey("project-report-delete", projectId, reportId),
      uuidErrorMessage: "Invalid project ID format. Please use a valid project ID.",
    }
  );
}
